/* iter-50p — Marketing-thumbnail editor (item 8).
 *
 * CRUD for promo tiles surfaced on both desktop and mobile landings.
 * Each row: image upload, title, caption, link-kind selector + target
 * input, sort_order, active toggle, optional starts_at/ends_at, save.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, ImagePlus, Save, ExternalLink } from 'lucide-react';
import { bookstoreAdminApi } from '@/lib/bookstoreApi';

const KINDS = [
  { v: 'none',     label: 'No link' },
  { v: 'book',     label: 'Book (id)' },
  { v: 'genre',    label: 'Genre (slug)' },
  { v: 'external', label: 'External URL' },
];

const EMPTY = {
  title: '', caption: '', image_url: '', link_kind: 'none',
  link_target: '', sort_order: 100, active: true, starts_at: '', ends_at: '',
};

export default function MarketingThumbnailEditor() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const d = await bookstoreAdminApi.listThumbnails();
      setRows(d.thumbnails || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load thumbnails');
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy((b) => ({ ...b, __new: true }));
    try {
      await bookstoreAdminApi.createThumbnail({ ...EMPTY, title: 'Untitled' });
      toast.success('Created');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setBusy((b) => { const n = { ...b }; delete n.__new; return n; });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this thumbnail? This is final.')) return;
    try {
      await bookstoreAdminApi.deleteThumbnail(id);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="bs-admin-thumbs" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight">Marketing thumbnails</h2>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Promo tiles surfaced on the landing pages and the mobile bookstore.
            Newest active rows render first; expired rows are skipped automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={create}
          disabled={!!busy.__new}
          data-testid="bs-admin-thumbs-add"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {busy.__new ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          New thumbnail
        </button>
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-card/40 p-6 text-center text-[12px] text-muted-foreground">
          No thumbnails yet. Click <strong>New thumbnail</strong> to create one.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => <ThumbRow key={row.id} row={row} onChanged={load} onRemove={() => remove(row.id)} />)}
        </div>
      )}
    </div>
  );
}

function ThumbRow({ row, onChanged, onRemove }) {
  const [draft, setDraft] = useState({ ...row, starts_at: row.starts_at || '', ends_at: row.ends_at || '' });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    setBusy(true);
    try {
      await bookstoreAdminApi.updateThumbnail(row.id, {
        ...draft,
        starts_at: draft.starts_at || null,
        ends_at:   draft.ends_at   || null,
      });
      toast.success('Saved');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const d = await bookstoreAdminApi.uploadThumbnailImage(row.id, f);
      setDraft((x) => ({ ...x, image_url: d.image_url }));
      toast.success('Image uploaded');
      await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="grid grid-cols-12 gap-3 rounded-md border border-border bg-card p-3" data-testid={`bs-admin-thumb-row-${row.id}`}>
      <div className="col-span-3 flex flex-col items-center gap-2">
        <div className="aspect-[3/2] w-full overflow-hidden rounded-sm border bg-[#f4e5c4]">
          {draft.image_url ? (
            <img src={draft.image_url} alt={draft.title || 'thumbnail'} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10.5px] text-muted-foreground">No image</div>
          )}
        </div>
        <input
          ref={fileRef} type="file" accept="image/*"
          onChange={upload} className="hidden"
          data-testid={`bs-admin-thumb-file-${row.id}`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={`bs-admin-thumb-upload-${row.id}`}
          className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary disabled:opacity-60"
        >
          <ImagePlus className="h-3 w-3" /> Upload image
        </button>
      </div>

      <div className="col-span-9 grid grid-cols-12 gap-2">
        <Field label="Title" className="col-span-6"
          value={draft.title}
          onChange={(v) => setDraft({ ...draft, title: v })}
          testid={`bs-admin-thumb-title-${row.id}`} />
        <Field label="Sort order (lower = first)" className="col-span-3"
          value={String(draft.sort_order || 0)}
          onChange={(v) => setDraft({ ...draft, sort_order: parseInt(v || '0', 10) })}
          testid={`bs-admin-thumb-sort-${row.id}`} />
        <label className="col-span-3 flex items-end gap-2 pb-1">
          <input type="checkbox" checked={!!draft.active}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            data-testid={`bs-admin-thumb-active-${row.id}`}
          />
          <span className="text-[12px]">Active</span>
        </label>

        <Field label="Caption (up to 240 chars)" className="col-span-12"
          value={draft.caption || ''}
          onChange={(v) => setDraft({ ...draft, caption: v.slice(0, 240) })}
          testid={`bs-admin-thumb-caption-${row.id}`} />

        <label className="col-span-3 flex flex-col gap-0.5">
          <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Link kind</span>
          <select
            value={draft.link_kind}
            onChange={(e) => setDraft({ ...draft, link_kind: e.target.value })}
            data-testid={`bs-admin-thumb-kind-${row.id}`}
            className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary"
          >
            {KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
          </select>
        </label>
        <Field label="Link target" className="col-span-9"
          placeholder={draft.link_kind === 'external' ? 'https://example.com' : draft.link_kind === 'book' ? 'book_id' : draft.link_kind === 'genre' ? 'genre-slug' : '—'}
          value={draft.link_target || ''}
          onChange={(v) => setDraft({ ...draft, link_target: v })}
          testid={`bs-admin-thumb-target-${row.id}`} />

        <Field label="Starts at (ISO, optional)" className="col-span-4"
          value={draft.starts_at || ''}
          placeholder="2026-03-01T00:00:00Z"
          onChange={(v) => setDraft({ ...draft, starts_at: v })}
          testid={`bs-admin-thumb-starts-${row.id}`} />
        <Field label="Ends at (ISO, optional)" className="col-span-4"
          value={draft.ends_at || ''}
          placeholder="2026-04-01T00:00:00Z"
          onChange={(v) => setDraft({ ...draft, ends_at: v })}
          testid={`bs-admin-thumb-ends-${row.id}`} />

        <div className="col-span-4 flex items-end justify-end gap-2">
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            data-testid={`bs-admin-thumb-delete-${row.id}`}
            className="inline-flex items-center gap-1 rounded-sm border border-rose-300/50 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-800 hover:border-rose-400 disabled:opacity-60"
          >
            <Trash2 className="h-3 w-3" /> Delete
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid={`bs-admin-thumb-save-${row.id}`}
            className="inline-flex items-center gap-1 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </button>
          {draft.link_kind === 'external' && draft.link_target && (
            <a
              href={draft.link_target}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`bs-admin-thumb-preview-${row.id}`}
              className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1.5 text-[11px] hover:bg-secondary"
            >
              <ExternalLink className="h-3 w-3" /> Open
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder = '', className = '' }) {
  return (
    <label className={`flex flex-col gap-0.5 ${className}`}>
      <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <input
        type="text"
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={testid}
        className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary"
      />
    </label>
  );
}
